import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { Ref, SetStateAction } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { useCheckbox } from '@tamagui/checkbox-headless';
import type { CheckedState } from '@tamagui/checkbox-headless';
import { isWeb, Stack, styled, View as TamaguiView, useProps } from '@tamagui/core';
import type { GestureReponderEvent } from '@tamagui/core';
import type { View } from 'react-native';

import { Checkmark } from '../../../__generated__/icons';
import type { AnalyticsProps } from '../../../helpers/analytics';
import { useObservabilitySafe } from '../../../helpers/useObservabilitySafe';
import { isKeyOfObject } from '../../../helpers/utils';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { useInputId } from '../shared/hooks';
import { CheckboxRadioLabel } from './shared/CheckboxRadioLabel';
import { getNextCheckedState, resolveCheckedState } from './shared/checkedState';
import { getCustomRadioCheckboxRowStyling, useCheckBoxRadioState } from './shared/hooks';
import type { SharedSingleCheckboxRadioProps } from './shared/types';

const { radioCheckbox: checkboxTokens } = tokens;

export type SingleCheckboxProps = SharedSingleCheckboxRadioProps &
  AnalyticsProps & {
    /**
     * Provides the parent component with a way to view the checked state of each checkbox
     */
    onPress?: (name: string, checked: CheckedState) => void;
    /**
     * If the checkbox is checked by default
     */
    defaultChecked?: CheckedState;
    /**
     * name value for the checkbox - used for checkbox groups
     */
    name?: string;
    /**
     * The current checked state of the checkbox. Use for noneOftheAbove option
     */
    checked?: CheckedState;
    /**
     * If true, hides the checkbox icon.
     */
    hiddenCheckIcon?: boolean;
  };

/**
 * Note: For multiple checkboxes use the Checkbox Group component (there are specific a11y requirements for groups of checkboxes which are handled by CheckboxGroup) <br/>
 * Checkbox provides a single checkbox component, users can check/uncheck the single option
 * The single checkbox is built with [Tamagui's headless checkbox](https://tamagui.dev/docs/components/checkbox?type=headless)
 *
 * **Meaningful Properties:**
 * - Pass an "onPress" function to get access to whether the checkbox is checked
 * - To pre-check the checkbox pass defaultChecked = true
 * - Pass invalid if you want the error state to show up and disabled to disable the checkbox
 *
 *
 * **Analytics:**
 * - Click Events: Checkbox click events will be tracked as ("checkboxClickEvent"), so `analyticsName` is required
 * - Impression Events: If you want to track impression events as ("checkboxViewEvent"), pass
 * `analyticsName` and `trackImpression` if you want to track the impression event
 *
 * **Accessibility:**
 *
 * Keyboard Interaction:
 * - When the checkbox has focus(can tab to focus), pressing the [space](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role#keyboard_interactions) key changes the state of the checkbox
 *
 * Checkbox:
 * - Has role [checkbox](https://w3c.github.io/aria/#checkbox)
 * - Focus indicator is visible and has sufficient contrast
 * - Value attribute attached
 * - [Aria-checked](https://developer.mozilla.org/en-US/docs/web/Accessibility/ARIA/Attributes/aria-checked) attribute updates based on if its checked or not
 * - Has an accessible label because the visible text content is contained within the element with role `checkbox`
 *
 */
export const SingleCheckbox = forwardRef<View, SingleCheckboxProps>(function SingleCheckbox(
  {
    analyticsName,
    label,
    value,
    subtitle,
    defaultChecked = false,
    disabled,
    invalid,
    id,
    onPress,
    name,
    trackImpression,
    checked,
    hiddenCheckIcon = false,
    required,
    renderLabel,
    ...props
  },
  ref: Ref<View>,
) {
  const { size = 'medium' } = useProps(props);

  const { trackJourneyEvent } = useObservabilitySafe();
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const internalCheckedRef = useRef<CheckedState>(defaultChecked);

  const setTrackedInternalChecked = useCallback((nextChecked: SetStateAction<CheckedState>) => {
    setInternalChecked((currentChecked) => {
      const resolvedChecked = resolveCheckedState(nextChecked, currentChecked);
      internalCheckedRef.current = resolvedChecked;
      return resolvedChecked;
    });
  }, []);

  useEffect(() => {
    if (checked !== undefined) {
      setTrackedInternalChecked(checked);
    }
  }, [checked, setTrackedInternalChecked]);

  const onCheckedChange = (e: GestureReponderEvent) => {
    e.stopPropagation();
    onPress?.(value, getNextCheckedState(internalCheckedRef.current));
    const data = { targetName: analyticsName, optionName: value };
    trackJourneyEvent('platform', 'checkboxClickEvent', data);
  };
  // https://tamagui.dev/docs/components/checkbox?type=headless
  const { checkboxProps, checkboxRef, bubbleInput } = useCheckbox(
    {
      name,
      value,
      defaultChecked,
      disabled,
      onPress: onCheckedChange,
      ...props,
    },
    [internalChecked, setTrackedInternalChecked],
    ref,
  );
  const inputId = useInputId(id);
  const { rowState, buttonState, handleFocus, handleBlur } = useCheckBoxRadioState({
    checked: internalChecked,
    disabled,
    invalid,
    onBlur: props.onBlur,
    onFocus: props.onFocus,
  });
  const { checkMarkSize, checkMarkColor } = getCheckMarkIconStyle(size, buttonState);
  const defaultLabel = (
    <CheckboxRadioLabel
      id={inputId}
      label={label}
      size={size}
      subtitle={subtitle}
      state={rowState}
    />
  );
  const labelContent = renderLabel ? renderLabel(defaultLabel) : defaultLabel;
  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      eventName="checkboxViewEvent"
    >
      <CheckboxRowButton
        {...checkboxProps}
        // do not pass required down to the CheckboxRowButton - validation to happen on onSubmit in the form
        aria-invalid={isWeb ? invalid : undefined}
        aria-required={isWeb ? required : undefined}
        ref={checkboxRef}
        size={size}
        state={rowState}
        onFocus={handleFocus}
        onBlur={handleBlur}
        focusable={!disabled}
        id={inputId}
      >
        {labelContent}
        {hiddenCheckIcon ? null : (
          <CheckMarkFrame
            disabled={disabled}
            size={size}
            state={{ state: buttonState, checked: internalChecked }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            // we only want to focus on the row button and not on the checkmark frame
            focusable={false}
          >
            {internalChecked === true && <Checkmark size={checkMarkSize} color={checkMarkColor} />}
            {bubbleInput}
          </CheckMarkFrame>
        )}
      </CheckboxRowButton>
    </TrackableContainer>
  );
});

/**
 * Components used in Checkbox:
 *
 * - Checkbox Row component:
 * This is treated as a button so we can:
 * 1) Add the right accessibility roles when this container is selected
 * 2) Enable onPress/onSpace to work on the container (checks/unchecks the checkbox)
 *
 * - CheckMarkFrame - styling for the box that holds the checkmark
 * - getCheckMarkIconStyle - gets the style for the checkmark
 */
const CheckboxRowButton = styled(TamaguiView, {
  tag: 'button',
  display: 'flex',
  flexDirection: 'row',
  gap: checkboxTokens.padding.textCheckboxGap.default,
  variants: {
    /**
     * Size is here to get types to work
     */
    size: (_size: SingleCheckboxProps['size']) => ({}),
    /**
     * State and size are used in the same function because there are multiple
     * times we need to use both to determine the style (especially for
     * small/xsmall)
     */
    state: (state: SingleCheckboxProps['state'], { props }) => {
      const { size } = props as SingleCheckboxProps;
      return getCustomRadioCheckboxRowStyling({ size, state });
    },
  } as const,
});

const CheckMarkFrame = styled(Stack, {
  borderWidth: checkboxTokens.borderWidth.default,
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  alignSelf: 'center',
  variants: {
    size: (size: SingleCheckboxProps['size']) => {
      const buttonSize = size === 'xsmall' || size === 'small' ? 'smallXsmall' : size;
      const widthAndHeight = {
        smallXsmall: 24,
        medium: 26,
        large: 32,
      };
      return {
        borderRadius: checkboxTokens.cornerRadius.element.checkbox[buttonSize],
        width: widthAndHeight[buttonSize],
        height: widthAndHeight[buttonSize],
      };
    },
    // eslint-disable-next-line jsdoc/require-jsdoc
    state: ({ state, checked }: { state: SingleCheckboxProps['state']; checked: CheckedState }) => {
      const borderColor = isKeyOfObject(state, checkboxTokens.color.border.element.checkbox.state)
        ? checkboxTokens.color.border.element.checkbox.state[state]
        : checkboxTokens.color.border.element.checkbox.default;

      const backgroundColor =
        (checked || state === 'inactive') &&
        isKeyOfObject(state, checkboxTokens.color.background.element.checkboxRadio.state)
          ? checkboxTokens.color.background.element.checkboxRadio.state[state]
          : '$transparent';
      return {
        borderColor,
        backgroundColor,
      };
    },
  } as const,
});
const getCheckMarkIconStyle = (
  size: SingleCheckboxProps['size'],
  state: SingleCheckboxProps['state'],
): {
  /**
   * The size of the checkmark
   */
  checkMarkSize: 20 | 24;
  /**
   * The color of the checkmark
   */
  checkMarkColor: Color;
} => {
  return {
    checkMarkSize: size === 'small' || size === 'xsmall' ? 20 : 24,
    checkMarkColor: isKeyOfObject(state, checkboxTokens.color.content.element.checkIcon.state)
      ? checkboxTokens.color.content.element.checkIcon.state[state]
      : checkboxTokens.color.content.element.checkIcon.default,
  };
};
